import { createSignal } from "solid-js";

import { closestSelector } from "./guards";
import { applyRubberBand, createGestureRecognizer } from "./recognizer";
import { pointerHandlers } from "./solid";
import { ROW_DELETE_SWIPE } from "./presets";

interface RowDeleteSwipeOptions {
  /** Commit handler: the row has been swiped left past the threshold. */
  onDelete: () => void;
  /** The sliding surface, used to measure how far to fling the row off-screen. */
  getSurfaceRef: () => HTMLElement | undefined;
  /**
   * When the whole row is itself a link/button (e.g. a recipe card), return true
   * so a drag may begin on it — a tap still navigates because the gesture only
   * engages past the deadzone with a horizontal axis lock. Defaults to false,
   * which keeps inner controls (a tappable delete button) out of the gesture.
   */
  allowControlDrag?: () => boolean;
}

const {
  thresholdPx: THRESHOLD_PX,
  commitThresholdPx: COMMIT_THRESHOLD_PX,
  rubberBandLimitPx: RUBBER_BAND_LIMIT,
  rubberBandFactor: RUBBER_BAND_FACTOR,
  hapticMs: HAPTIC_VIBRATION_MS,
  commitAnimationMs: COMMIT_ANIMATION_MS,
  cancelAnimationMs: CANCEL_ANIMATION_MS,
  fallbackWidthPx: FALLBACK_WIDTH_PX,
} = ROW_DELETE_SWIPE;

/**
 * Generic per-row swipe-to-delete, sharing the family-wide row physics
 * (deadzone, axis-lock, rubber-band, fling, rising haptic at the commit
 * threshold). Only the left direction commits — a right drag rubber-bands and
 * bounces back — so this is the single-action sibling of the shopping-row hook.
 *
 * Drags that begin on a control (button / link / input) are ignored so the
 * keyboard / click path stays intact, and the gesture only claims the
 * horizontal axis (bind it on an element with `touch-action: pan-y`).
 */
export function useRowDeleteSwipe(options: RowDeleteSwipeOptions) {
  const [tx, setTx] = createSignal(0);
  const [isSwiping, setIsSwiping] = createSignal(false);
  const [isAnimating, setIsAnimating] = createSignal(false);

  let hasSwiped = false;

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: THRESHOLD_PX,
    commitPx: COMMIT_THRESHOLD_PX,
    haptics: true,
    hapticMs: HAPTIC_VIBRATION_MS,
    canStart: ({ target }) => {
      if (closestSelector(target, '[data-no-swipe="true"]')) {
        return false;
      }
      if (options.allowControlDrag?.()) {
        return true;
      }
      return !closestSelector(target, "button, a, input");
    },
    onStart: () => {
      setIsSwiping(true);
      setIsAnimating(false);
    },
    onMove: (sample) => {
      // Only allow left travel; a rightward drag stays clamped near center.
      const clamped = Math.min(sample.dx, 0);
      setTx(applyRubberBand(clamped, RUBBER_BAND_LIMIT, RUBBER_BAND_FACTOR));
    },
    onEnd: () => {
      hasSwiped = true;
      setIsSwiping(false);
      setIsAnimating(true);

      const finalTx = tx();
      if (finalTx < -COMMIT_THRESHOLD_PX) {
        // Swipe left: slide the row fully out, then delete.
        const width = options.getSurfaceRef()?.clientWidth ?? FALLBACK_WIDTH_PX;
        setTx(-width);
        setTimeout(() => {
          options.onDelete();
          setTx(0);
          setIsAnimating(false);
        }, COMMIT_ANIMATION_MS);
      } else {
        // Cancel and bounce back to center.
        setTx(0);
        setTimeout(() => {
          setIsAnimating(false);
        }, CANCEL_ANIMATION_MS);
      }
    },
    onCancel: () => {
      setIsSwiping(false);
      setIsAnimating(true);
      setTx(0);
      setTimeout(() => {
        setIsAnimating(false);
      }, CANCEL_ANIMATION_MS);
    },
  });

  const base = pointerHandlers(recognizer);

  return {
    tx,
    isSwiping,
    isAnimating,
    handlePointerDown: base.onPointerDown,
    handlePointerMove: base.onPointerMove,
    handlePointerUp: base.onPointerUp,
    handlePointerCancel: base.onPointerCancel,
    getHasSwiped: () => hasSwiped,
    resetHasSwiped: () => {
      hasSwiped = false;
    },
    COMMIT_THRESHOLD_PX,
    CANCEL_ANIMATION_MS,
  };
}
